import { useCallback, useEffect, useRef, useState } from "react";
import api from "../services/api";
import { resolveCaptacionMediaUrl } from "../services/captacionUpload";
import { captacionUploadQueue } from "../services/captacionUploadQueue";
import { mostrarAvisoEncargoNoValidado } from "../utils/avisoEncargoNoValidado";
import { useAuth } from "../context/AuthContext";

const UUID_RX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Sincroniza la ficha con la cola global de subidas (sigue activa al salir del formulario).
 */
export function useCaptacionSubidaFondo({
  captacionId,
  enabled,
  fotos,
  setFotos,
  documentos,
  setDocumentos,
  videosArchivo,
  setVideosArchivo,
  documentosEliminar,
  setDocumentosEliminar,
  idsReportajeInicialRef,
}) {
  const { usuario } = useAuth();
  const [stats, setStats] = useState({
    pendientes: 0,
    subiendo: 0,
    errores: 0,
    completados: 0,
    mensajesError: [],
  });
  const avisosEncargoMostrados = useRef(new Set());

  const fotosRef = useRef(fotos);
  const documentosRef = useRef(documentos);
  const videosRef = useRef(videosArchivo);
  const eliminarDocsRef = useRef(documentosEliminar);
  const usuarioRef = useRef(usuario);
  usuarioRef.current = usuario;

  fotosRef.current = fotos;
  documentosRef.current = documentos;
  videosRef.current = videosArchivo;
  eliminarDocsRef.current = documentosEliminar;

  const refreshStats = useCallback(() => {
    if (!captacionId) return;
    setStats(captacionUploadQueue.getStats(captacionId));
  }, [captacionId]);

  useEffect(() => {
    if (!enabled || !captacionId) return undefined;

    captacionUploadQueue.attachUI(captacionId, {
      onItemUpdate: (tipo, localId, patch) => {
        if (tipo === "foto") {
          setFotos((prev) =>
            prev.map((f) => {
              if (f.id !== localId) return f;
              if (patch.subidaEstado === "ok" && patch.server) {
                const s = patch.server;
                idsReportajeInicialRef?.current?.add(s.id);
                const thumb = s.url_thumb ? resolveCaptacionMediaUrl(s.url_thumb) : null;
                const display = resolveCaptacionMediaUrl(s.url);
                return {
                  id: s.id,
                  url: s.url,
                  url_thumb: s.url_thumb || null,
                  preview: thumb || display,
                  thumbPreview: thumb,
                  existente: true,
                  tipo: f.tipo || "publica",
                  descripcion_es: f.descripcion_es || "",
                  marco_autoenhance: f.marco_autoenhance !== false,
                  orden: f.orden,
                  subidaEstado: "ok",
                  file: undefined,
                };
              }
              return { ...f, ...patch };
            })
          );
        } else if (tipo === "doc") {
          setDocumentos((prev) =>
            prev.map((d) => {
              if (d.id !== localId) return d;
              const rev = patch.encargoRevision || patch.server?.encargoRevision;
              if (
                (patch.subidaEstado === "ok" || patch.subidaEstado === "error")
                && rev
                && rev.ok === false
              ) {
                const avisoKey = `${captacionId}:${localId}:${rev.motivo || rev.mensaje || "x"}`;
                if (!avisosEncargoMostrados.current.has(avisoKey)) {
                  avisosEncargoMostrados.current.add(avisoKey);
                  const fileForzar = d.file || null;
                  const metaForzar = {
                    nombre_documento: d.nombre_documento || fileForzar?.name,
                    tipo_codigo: d.tipo_codigo || "nota_encargo",
                    descripcion_tipo: d.descripcion_tipo || null,
                  };
                  // Diferir al siguiente tick: no bloquear el setState del map.
                  setTimeout(() => {
                    mostrarAvisoEncargoNoValidado({
                      mensaje: rev.mensaje || patch.subidaError,
                      rev,
                      usuario: usuarioRef.current,
                      captacionId,
                      file: fileForzar,
                      meta: metaForzar,
                      onTrasForzarOk: (docOk) => {
                        setDocumentos((cur) =>
                          cur.map((x) =>
                            x.id === localId || x.id === patch.server?.id
                              ? {
                                  ...docOk,
                                  existente: true,
                                  preview: docOk.url,
                                  subidaEstado: "ok",
                                  file: undefined,
                                  tipo_codigo: docOk.tipo_codigo || "nota_encargo",
                                }
                              : x
                          )
                        );
                      },
                    });
                  }, 0);
                }
              }
              if (patch.subidaEstado === "ok" && patch.server) {
                return {
                  ...patch.server,
                  existente: true,
                  preview: patch.server.url,
                  subidaEstado: "ok",
                  // Conservar File si secretaría puede autorizar después.
                  file: patch.keepFileForForzar ? d.file : undefined,
                  encargoRevision: rev || undefined,
                };
              }
              return { ...d, ...patch };
            })
          );
        } else if (tipo === "vid") {
          setVideosArchivo((prev) =>
            prev.map((v) => {
              if (v.id !== localId) return v;
              if (patch.subidaEstado === "ok" && patch.server) {
                const s = patch.server;
                if (v.preview) URL.revokeObjectURL(v.preview);
                return {
                  id: s.id,
                  url: s.url,
                  preview: resolveCaptacionMediaUrl(s.url),
                  titulo: v.titulo || "",
                  existente: true,
                  subidaEstado: "ok",
                  file: undefined,
                };
              }
              return { ...v, ...patch };
            })
          );
        }
        refreshStats();
      },
    });

    return () => captacionUploadQueue.detachUI(captacionId);
  }, [captacionId, enabled, refreshStats, setDocumentos, setFotos, setVideosArchivo, idsReportajeInicialRef]);

  useEffect(() => {
    if (!enabled || !captacionId) return undefined;
    const unsub = captacionUploadQueue.subscribe(() => refreshStats());
    refreshStats();
    return unsub;
  }, [captacionId, enabled, refreshStats]);

  useEffect(() => {
    if (!enabled || !captacionId) return;

    setFotos((prev) =>
      prev.map((f) =>
        f.file && !f.subidaEstado ? { ...f, subidaEstado: "pendiente" } : f
      )
    );
    setDocumentos((prev) =>
      prev.map((d) =>
        d.file && !d.subidaEstado ? { ...d, subidaEstado: "pendiente" } : d
      )
    );
    setVideosArchivo((prev) =>
      prev.map((v) =>
        v.file && !v.subidaEstado ? { ...v, subidaEstado: "pendiente" } : v
      )
    );
  }, [captacionId, enabled, fotos.length, documentos.length, videosArchivo.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enabled || !captacionId) return;

    const fotoIds = new Set(fotos.filter((f) => f.file && f.subidaEstado !== "ok").map((f) => f.id));
    const docIds = new Set(documentos.filter((d) => d.file && d.subidaEstado !== "ok").map((d) => d.id));
    const vidIds = new Set(videosArchivo.filter((v) => v.file && v.subidaEstado !== "ok").map((v) => v.id));

    for (const f of fotos) {
      if (f.file && f.subidaEstado !== "ok") captacionUploadQueue.upsertFoto(captacionId, f);
    }
    for (const d of documentos) {
      if (d.file && d.subidaEstado !== "ok") captacionUploadQueue.upsertDocumento(captacionId, d);
    }
    for (const v of videosArchivo) {
      if (v.file && v.subidaEstado !== "ok") captacionUploadQueue.upsertVideo(captacionId, v);
    }

    captacionUploadQueue.pruneStaleJobs(captacionId, { fotoIds, docIds, vidIds });

    refreshStats();
  }, [enabled, captacionId, fotos, documentos, videosArchivo, refreshStats]);

  const hayPendientes = useCallback(() => {
    const s = captacionUploadQueue.getStats(captacionId);
    return s.pendientes > 0 || s.subiendo > 0;
  }, [captacionId]);

  const hayErrores = useCallback(() => {
    return captacionUploadQueue.getStats(captacionId).errores > 0;
  }, [captacionId]);

  const reintentarErrores = useCallback(() => {
    if (!captacionId) return;
    captacionUploadQueue.reintentarErrores(captacionId);
    setFotos((prev) =>
      prev.map((f) =>
        f.subidaEstado === "error" ? { ...f, subidaEstado: "pendiente", subidaError: undefined } : f
      )
    );
    setDocumentos((prev) =>
      prev.map((d) =>
        d.subidaEstado === "error" ? { ...d, subidaEstado: "pendiente", subidaError: undefined } : d
      )
    );
    setVideosArchivo((prev) =>
      prev.map((v) =>
        v.subidaEstado === "error" ? { ...v, subidaEstado: "pendiente", subidaError: undefined } : v
      )
    );
    refreshStats();
  }, [captacionId, refreshStats, setDocumentos, setFotos, setVideosArchivo]);

  const sincronizarMetadatos = useCallback(async () => {
    if (!captacionId) return;

    const actualesReportajeIds = fotosRef.current
      .filter((f) => f.existente && f.id && UUID_RX.test(String(f.id)))
      .map((f) => f.id);

    if (idsReportajeInicialRef?.current) {
      for (const fid of idsReportajeInicialRef.current) {
        if (!actualesReportajeIds.includes(fid)) {
          try {
            await api.delete(`/captaciones/fotos-reportaje/${fid}`);
          } catch {
            /* ignore */
          }
        }
      }
    }

    const orden = fotosRef.current
      .map((f) => f.id)
      .filter((id) => typeof id === "string" && UUID_RX.test(id));
    if (orden.length > 0) {
      try {
        await api.put(`/captaciones/${captacionId}/fotos-reportaje/orden`, { orden });
      } catch {
        /* ignore */
      }
    }

    const flags = fotosRef.current
      .filter((f) => f.existente && f.id && UUID_RX.test(String(f.id)))
      .map((f) => ({ id: f.id, marco_autoenhance: f.marco_autoenhance !== false }));
    if (flags.length > 0) {
      try {
        await api.put(`/captaciones/${captacionId}/fotos-reportaje/marco-autoenhance`, { flags });
      } catch {
        /* ignore */
      }
    }

    for (const docId of eliminarDocsRef.current) {
      try {
        await api.delete(`/captaciones/documentos/${docId}`);
      } catch {
        /* ignore */
      }
    }

    const existentesDoc = documentosRef.current.filter((d) => d.existente && d.id);
    await Promise.all(
      existentesDoc.map((d) =>
        api
          .patch(`/captaciones/documentos/${d.id}`, {
            tipo_codigo: d.tipo_codigo,
            descripcion_tipo: d.descripcion_tipo || null,
            nombre_documento: d.nombre_documento,
          })
          .catch(() => {})
      )
    );

    setDocumentosEliminar([]);
  }, [captacionId, idsReportajeInicialRef, setDocumentosEliminar]);

  const prepararFinalizarConSubidasPendientes = useCallback(() => {
    if (!captacionId) return;
    const syncMeta = captacionUploadQueue.buildSyncMeta({
      fotos: fotosRef.current,
      documentos: documentosRef.current,
      documentosEliminar: eliminarDocsRef.current,
      idsReportajeInicial: idsReportajeInicialRef?.current,
    });
    captacionUploadQueue.marcarFinalizarPendiente(captacionId, syncMeta);
  }, [captacionId, idsReportajeInicialRef]);

  return {
    stats,
    hayPendientes,
    hayErrores,
    reintentarErrores,
    sincronizarMetadatos,
    prepararFinalizarConSubidasPendientes,
  };
}
